import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { useState } from "react";
import { sendSupportEmailAPI } from "../../components/Terms/Utils/supportEmailAPI"; // Fixed import path
import SEO from "../SEO/SEO"

const SupportPage = () => {
  const [activeTab, setActiveTab] = useState("technical");
  const [formData, setFormData] = useState({
    fullName: "",
    company: "",
    email: "",
    phone: "",
    supportCategory: "",
    machineDetails: "",
    issueDescription: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  console.log("🔄 [SupportPage.js] Component rendered");
  console.log("🔄 [SupportPage.js] Form data:", formData);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`📝 [SupportPage.js] Input changed - ${name}: ${value}`);

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🚀 [SupportPage.js] Form submission started");

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      console.log("📧 [SupportPage.js] Calling sendSupportEmailAPI...");

      // Send support request using your backend API
      await sendSupportEmailAPI(formData);

      console.log("✅ [SupportPage.js] Support request submitted successfully");
      setSubmitStatus("success");

      // Reset form
      setFormData({
        fullName: "",
        company: "",
        email: "",
        phone: "",
        supportCategory: "",
        machineDetails: "",
        issueDescription: "",
      });
    } catch (error) {
      console.error(
        "❌ [SupportPage.js] Error submitting support request:",
        error,
      );
      setSubmitStatus("error");
    } finally {
      console.log("🏁 [SupportPage.js] Form submission process completed");
      setIsSubmitting(false);
    }
  };

  const supportServices = [
    {
      icon: "🔧",
      title: "Technical Support",
      description:
        "Get expert assistance for machinery operation, troubleshooting, and technical issues",
      features: [
        "Machine setup and installation guidance",
        "Operation and maintenance training",
        "Troubleshooting and diagnostics",
        "Performance optimization",
        "Software and firmware support",
        "Remote assistance available",
      ],
      contact: "techsupport@nexoramachinery.com",
    },
    {
      icon: "🛠️",
      title: "Spare Parts & Service",
      description:
        "Genuine spare parts, maintenance services, and repair support for your equipment",
      features: [
        "Genuine OEM spare parts",
        "Preventive maintenance schedules",
        "Emergency repair services",
        "Parts availability checking",
        "Express shipping options",
        "On-site service teams",
      ],
      contact: "parts@nexoramachinery.com",
    },
    {
      icon: "📞",
      title: "Customer Service",
      description:
        "Order tracking, account management, and general inquiries assistance",
      features: [
        "Order status and tracking",
        "Account and billing support",
        "Product information",
        "Warranty claims processing",
        "Documentation requests",
        "General inquiries",
      ],
      contact: "customerservice@nexoramachinery.com",
    },
  ];

  const faqCategories = {
    technical: [
      {
        question: "How do I set up my new CNC machine?",
        answer:
          "We provide comprehensive installation guides and can schedule a technical specialist for on-site setup. Most machines include step-by-step setup documentation and video tutorials.",
      },
      {
        question: "What maintenance schedule should I follow?",
        answer:
          "Maintenance schedules vary by equipment type. Generally, we recommend daily inspections, weekly lubrication checks, and monthly comprehensive maintenance. Refer to your equipment manual for specific intervals.",
      },
    ],
    parts: [
      {
        question: "How do I order genuine spare parts?",
        answer:
          "You can order spare parts through your account manager, via email at parts@nexora.com, or through our online parts portal. Include your machine model and serial number for fastest service.",
      },
    ],
    general: [
      {
        question: "What are your support hours?",
        answer:
          "Technical Support: Mon-Fri 7:00 AM - 7:00 PM (Local Time), Emergency Support: 24/7 for critical issues. Customer Service: Mon-Fri 8:00 AM - 6:00 PM.",
      },
    ],
  };

  const emergencyContacts = [
    {
      type: "Technical Emergency",
      phone: "+1 (555) 123-TECH",
      hours: "24/7",
      description: "For critical machine breakdowns affecting production",
    },
  ];

  return (
    <>
      <Header />
      <SEO
        title="Support Center | Nexora Machinery"
        description="Get technical support, spare parts, and customer service for your industrial machinery and equipment from Nexora experts."
        keywords="About Nexora Machinery, CNC Machine Supplier"
        path="/Support"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white pt-24 pb-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            Support <span className="text-cyan-400">Center</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-6">
            Expert Technical Support & Customer Service
          </p>
          <div className="w-20 h-1 bg-cyan-400 mx-auto"></div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="bg-red-600 text-white py-4">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">🚨</span>
              <span className="font-semibold">Emergency Support Available</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span>
                Critical Breakdowns: <strong>+1 (555) 123-TECH</strong>
              </span>
              <span className="hidden sm:inline">•</span>
              <span>24/7 Technical Assistance</span>
            </div>
          </div>
        </div>
      </section>

      {/* Support Services */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wide">
              Our Services
            </span>
            <h2 className="text-3xl font-bold text-slate-800 mt-2 mb-4">
              Comprehensive Support Solutions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From technical assistance to spare parts, we're here to keep your
              operations running smoothly
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {supportServices.map((service, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-4">{service.description}</p>

                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm text-slate-600 mb-2">Contact:</p>
                  <a
                    href={`mailto:${service.contact}`}
                    className="text-cyan-600 font-semibold hover:text-cyan-700"
                  >
                    {service.contact}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wide">
              Help Center
            </span>
            <h2 className="text-3xl font-bold text-slate-800 mt-2 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {Object.keys(faqCategories).map((category) => (
              <button
                key={category}
                onClick={() => setActiveTab(category)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === category
                    ? "bg-cyan-500 text-white"
                    : "bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          {/* FAQ Content */}
          <div className="space-y-4">
            {faqCategories[activeTab].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm border border-slate-200"
              >
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  {faq.question}
                </h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contacts */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wide">
              Emergency Support
            </span>
            <h2 className="text-3xl font-bold text-slate-800 mt-2 mb-4">
              24/7 Emergency Contacts
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              For critical issues affecting your production operations
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {emergencyContacts.map((contact, index) => (
              <div
                key={index}
                className="bg-red-50 rounded-xl p-6 border border-red-200 text-center"
              >
                <div className="text-2xl mb-3">🚨</div>
                <h3 className="text-lg font-bold text-red-800 mb-2">
                  {contact.type}
                </h3>
                <p className="text-2xl font-bold text-red-600 mb-2">
                  {contact.phone}
                </p>
                <p className="text-sm text-red-700 mb-2">
                  Available: {contact.hours}
                </p>
                <p className="text-sm text-red-600">{contact.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Need Additional Help?</h2>
            <p className="text-slate-300 text-lg">
              Send us a detailed message and our support team will get back to
              you within 24 hours
            </p>
          </div>

          <div className="bg-slate-700 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-slate-600 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-slate-600 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400"
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-slate-600 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400"
                    placeholder="your.email@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg bg-slate-600 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Support Category *
                </label>
                <select
                  name="supportCategory"
                  value={formData.supportCategory}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-slate-600 border border-slate-500 text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="">Select support category</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Spare Parts">Spare Parts</option>
                  <option value="Maintenance Service">
                    Maintenance Service
                  </option>
                  <option value="Warranty Claim">Warranty Claim</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Emergency Support">Emergency Support</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Machine Details
                </label>
                <input
                  type="text"
                  name="machineDetails"
                  value={formData.machineDetails}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg bg-slate-600 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400"
                  placeholder="Machine model, serial number, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Issue Description *
                </label>
                <textarea
                  rows="4"
                  name="issueDescription"
                  value={formData.issueDescription}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-slate-600 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400"
                  placeholder="Please describe your issue in detail..."
                ></textarea>
              </div>

              {/* Status Messages */}
              {submitStatus === "success" && (
                <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg">
                  ✅ Thank you! Your support request has been submitted. We'll
                  get back to you within 24 hours.
                </div>
              )}

              {submitStatus === "error" && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
                  ❌ Sorry, there was an error submitting your request. Please
                  try again or contact us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-lg font-semibold transition-colors duration-300 ${
                  isSubmitting
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-cyan-500 hover:bg-cyan-600"
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </span>
                ) : (
                  "Submit Support Request"
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SupportPage;
